import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ListTodo, Plus, Search, X, Calendar, User, Flag,
  Clock, CheckCircle2, Circle, AlertCircle, MoreVertical, GripVertical
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../../config/api';

const statusColumns = {
  todo: { label: 'À faire', color: 'bg-gray-100', headerColor: 'bg-gray-200' },
  in_progress: { label: 'En cours', color: 'bg-blue-100', headerColor: 'bg-blue-200' },
  review: { label: 'En révision', color: 'bg-amber-100', headerColor: 'bg-amber-200' },
  done: { label: 'Terminé', color: 'bg-green-100', headerColor: 'bg-green-200' }
};

const priorityLabels = {
  low: { label: 'Basse', color: 'text-gray-500', bgColor: 'bg-gray-100' },
  medium: { label: 'Moyenne', color: 'text-amber-600', bgColor: 'bg-amber-100' },
  high: { label: 'Haute', color: 'text-red-600', bgColor: 'bg-red-100' },
  urgent: { label: 'Urgente', color: 'text-red-700', bgColor: 'bg-red-200' }
};

export default function AdminTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [draggedTask, setDraggedTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignee_id: '',
    priority: 'medium',
    due_date: '',
    status: 'todo'
  });

  useEffect(() => {
    fetchTeamMembers();
    fetchTasks();
  }, []);

  const fetchTeamMembers = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/assistants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const members = [
          { id: 'admin', name: 'Admin (Moi)' },
          ...data.map(a => ({ id: a.id, name: a.name }))
        ];
        setTeamMembers(members);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchTasks = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin-crm/tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      } else {
        // Use mock tasks if API doesn't exist
        setTasks([
          { id: '1', title: 'Bienvenue dans les tâches', description: 'Crée tes premières tâches pour organiser le travail de ton équipe', status: 'todo', priority: 'medium', assignee_name: 'Admin', created_at: new Date().toISOString() }
        ]);
      }
    } catch (error) {
      setTasks([
        { id: '1', title: 'Bienvenue dans les tâches', description: 'Crée tes premières tâches pour organiser le travail de ton équipe', status: 'todo', priority: 'medium', assignee_name: 'Admin', created_at: new Date().toISOString() }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }

    const token = localStorage.getItem('admin_token');
    
    // Get assignee name
    const assignee = teamMembers.find(m => m.id === formData.assignee_id);
    
    const newTask = {
      id: selectedTask?.id || Date.now().toString(),
      ...formData,
      assignee_name: assignee?.name || 'Non assigné',
      created_at: selectedTask?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    try {
      const url = selectedTask 
        ? `${API_URL}/api/admin-crm/tasks/${selectedTask.id}`
        : `${API_URL}/api/admin-crm/tasks`;
      
      const response = await fetch(url, {
        method: selectedTask ? 'PUT' : 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(selectedTask ? 'Tâche mise à jour' : 'Tâche créée');
        fetchTasks();
      } else {
        // Optimistic update
        if (selectedTask) {
          setTasks(prev => prev.map(t => t.id === selectedTask.id ? newTask : t));
        } else {
          setTasks(prev => [...prev, newTask]);
        }
        toast.success(selectedTask ? 'Tâche mise à jour' : 'Tâche créée');
      }
    } catch (error) {
      // Optimistic update
      if (selectedTask) {
        setTasks(prev => prev.map(t => t.id === selectedTask.id ? newTask : t));
      } else {
        setTasks(prev => [...prev, newTask]);
      }
      toast.success(selectedTask ? 'Tâche mise à jour' : 'Tâche créée');
    }

    setShowModal(false);
    resetForm();
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Supprimer cette tâche ?')) return;

    const token = localStorage.getItem('admin_token');
    setTasks(prev => prev.filter(t => t.id !== taskId));
    
    try {
      await fetch(`${API_URL}/api/admin-crm/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (error) {
      // Already deleted from state
    }
    toast.success('Tâche supprimée');
  };

  const handleDragStart = (task) => {
    setDraggedTask(task);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (status) => {
    if (draggedTask && draggedTask.status !== status) {
      const updatedTask = { ...draggedTask, status };
      setTasks(prev => prev.map(t => t.id === draggedTask.id ? updatedTask : t));
      
      // Update in backend
      const token = localStorage.getItem('admin_token');
      fetch(`${API_URL}/api/admin-crm/tasks/${draggedTask.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      }).catch(() => {});
      
      toast.success('Tâche déplacée');
    }
    setDraggedTask(null);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      assignee_id: '',
      priority: 'medium',
      due_date: '',
      status: 'todo'
    });
    setSelectedTask(null);
  };

  const openEditModal = (task) => {
    setSelectedTask(task);
    setFormData({
      title: task.title || '',
      description: task.description || '',
      assignee_id: task.assignee_id || '',
      priority: task.priority || 'medium',
      due_date: task.due_date || '',
      status: task.status || 'todo'
    });
    setShowModal(true);
  };

  const getTasksByStatus = (status) => {
    return tasks.filter(t => t.status === status);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="admin-tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tâches</h1>
          <p className="text-gray-500">Gérez les tâches de ton équipe</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-task-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle tâche
        </Button>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-4 h-full min-w-max pb-4">
          {Object.entries(statusColumns).map(([status, { label, color, headerColor }]) => (
            <div
              key={status}
              className="w-80 flex-shrink-0 bg-gray-50 rounded-xl overflow-hidden flex flex-col"
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(status)}
              data-testid={`column-${status}`}
            >
              {/* Column Header */}
              <div className={`${headerColor} p-3`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">{label}</span>
                    <span className="bg-white/50 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                      {getTasksByStatus(status).length}
                    </span>
                  </div>
                  <button 
                    onClick={() => { resetForm(); setFormData(prev => ({ ...prev, status })); setShowModal(true); }}
                    className="p-1 hover:bg-white/30 rounded transition-colors"
                  >
                    <Plus className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Tasks */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {getTasksByStatus(status).map(task => (
                  <motion.div
                    key={task.id}
                    draggable
                    onDragStart={() => handleDragStart(task)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-white rounded-lg p-3 shadow-sm border border-gray-100 cursor-move hover:shadow-md transition-shadow ${
                      draggedTask?.id === task.id ? 'opacity-50' : ''
                    }`}
                    data-testid={`task-${task.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-gray-900 text-sm line-clamp-2">{task.title}</h3>
                      <div className="flex items-center">
                        <button
                          onClick={() => openEditModal(task)}
                          className="p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-400" />
                        </button>
                      </div>
                    </div>
                    
                    {task.description && (
                      <p className="text-xs text-gray-500 mb-3 line-clamp-2">{task.description}</p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${priorityLabels[task.priority]?.bgColor} ${priorityLabels[task.priority]?.color}`}>
                          {priorityLabels[task.priority]?.label}
                        </span>
                        {task.due_date && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(task.due_date)}
                          </span>
                        )}
                      </div>
                      {task.assignee_name && (
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center" title={task.assignee_name}>
                          <span className="text-xs text-blue-600 font-medium">
                            {task.assignee_name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-lg shadow-xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="task-modal"
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold">{selectedTask ? 'Modifier la tâche' : 'Nouvelle tâche'}</h2>
                  <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Titre de la tâche..."
                    data-testid="task-title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Description de la tâche..."
                    data-testid="task-description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assigné à</label>
                    <select
                      value={formData.assignee_id}
                      onChange={(e) => setFormData({...formData, assignee_id: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="task-assignee"
                    >
                      <option value="">Non assigné</option>
                      {teamMembers.map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="task-priority"
                    >
                      {Object.entries(priorityLabels).map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date d'échéance</label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="task-due-date"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      data-testid="task-status"
                    >
                      {Object.entries(statusColumns).map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t border-gray-100">
                  {selectedTask && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => { handleDeleteTask(selectedTask.id); setShowModal(false); }}
                    >
                      Supprimer
                    </Button>
                  )}
                  <div className={`flex gap-3 ${!selectedTask ? 'w-full justify-end' : ''}`}>
                    <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                      Annuler
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-task-btn">
                      {selectedTask ? 'Modifier' : 'Créer'}
                    </Button>
                  </div>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
